'use client';
import React, { useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth, UserRole } from '@/context/AuthContext';

// Public routes that don't require auth
const PUBLIC_ROUTES = ['/login', '/payment'];

// Role-based default landing pages
export const ROLE_HOME: Record<UserRole, string> = {
  super_admin: '/super-admin',
  company_admin: '/',
  client: '/client-portal',
  mechanic: '/mechanic-portal',
};

// Routes each role is allowed to access
const ROLE_ALLOWED: Record<UserRole, string[]> = {
  super_admin: ['/super-admin'],
  company_admin: ['/', '/job-card-management', '/client-portal', '/clients-management', '/master-data', '/mechanic-portal', '/notifications', '/settings'],
  client: ['/client-portal'],
  mechanic: ['/mechanic-portal'],
};

export default function RouteGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (isLoading) return;

    const isPublic = PUBLIC_ROUTES.includes(pathname);

    if (!user) {
      if (!isPublic) {
        router.replace('/login');
      }
      return;
    }

    // Logged in — redirect away from login
    if (isPublic) {
      router.replace(ROLE_HOME[user.role]);
      return;
    }

    // Check role access
    const allowed = ROLE_ALLOWED[user.role];
    const hasAccess = allowed.some((route) => pathname === route || pathname.startsWith(route + '/'));
    if (!hasAccess) {
      router.replace(ROLE_HOME[user.role]);
    }
  }, [user, isLoading, pathname, router]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  const isPublic = PUBLIC_ROUTES.includes(pathname);
  if (!user && !isPublic) return null;

  return <>{children}</>;
}
